import * as React from "react";
import styled from "styled-components";
import {
  RepeatIcon,
  icons,
  ListIcon,
  FlagIcon,
  WorkflowIcon,
  ArchiveIcon,
  CalendarIcon,
  CreditCardIcon,
  XIcon,
  MenuIcon,
  ListCheckIcon,
} from "lucide-react";
import { useIsoContext } from "../isoContext";
import classNames from "classnames";
import { useLocation } from "wouter";
import { Section } from "src/components/Nav/Section";
import { Item } from "src/components/Nav/Item";
import { getLocalDate } from "src/database/utils/localDate";

export interface Props {
  action?:
    | {
        path: string;
        label?: string;
        icon: (typeof icons)[keyof typeof icons];
      }
    | {
        fn: () => void;
        label?: string;
        icon: (typeof icons)[keyof typeof icons];
      };
}

function NavInner() {
  const today = getLocalDate(new Date());

  return (
    <NavInnerRoot data-testid="layout-nav">
      <Section label="Planning">
        <Item icon={ListCheckIcon} to={`/tasks/${today}`} label="Todo List" />
        <Item label="Calendar" icon={CalendarIcon} to={`/calendar/${getLocalDate(new Date())}`} />
        <Item label="Habits" icon={RepeatIcon} to="/habits" />
        <Item label="Backlog" icon={ListIcon} to="/backlog" />
        <Item label="Goals" icon={FlagIcon} to="/goals" />
      </Section>
      <Section label="Finances">
        <Item label="Transactions" icon={CreditCardIcon} to="/finances/transactions" />
        <Item label="Rules" icon={WorkflowIcon} to="/finances/rules" />
        <Item label="Categories" icon={ArchiveIcon} to="/finances/categories" />
      </Section>
    </NavInnerRoot>
  );
}

export function Nav({ action }: Props) {
  const { isLarge } = useIsoContext();
  const [navOpen, setNavOpen] = React.useState(false);
  const [, navigate] = useLocation();

  if (isLarge) {
    return (
      <DesktopNav className={classNames({ "menu-large": isLarge, "menu-open": true })}>
        <NavInner />
      </DesktopNav>
    );
  }
  return (
    <>
      <NavHeader className={navOpen ? "menu-open" : undefined}>
        {action && (
          <MenuAction onClick={"path" in action ? () => navigate(action.path) : action.fn}>
            <action.icon color="#ccc" strokeWidth={1.5} size={18} />
            {action.label && <MenuActionLabel>{action.label}</MenuActionLabel>}
          </MenuAction>
        )}
        <Spacer />
        <Toggle onClick={() => setNavOpen(!navOpen)}>
          {navOpen ? (
            <XIcon color="#ccc" strokeWidth={1.5} size={20} />
          ) : (
            <MenuIcon color="#ccc" strokeWidth={1.5} size={20} />
          )}
        </Toggle>
      </NavHeader>
      <MobileNav className={navOpen ? "menu-open" : undefined}>
        <NavInner />
      </MobileNav>
    </>
  );
}

const NavHeader = styled.header`
  display: flex;
  flex-direction: row;
  position: relative;
  z-index: 10;
  height: 44px;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.125);
  background: var(--theme-color-primary);
  &.menu-open {
    background: var(--theme-color-primary);
`;

const Spacer = styled.div`
  flex-grow: 1;
`;

const MobileNav = styled.div`
  position: fixed;
  display: none;
  z-index: 10;
  left: 0;
  top: 44px;
  width: 100%;
  height: 100%;
  overflow: auto;
  background: var(--theme-color-primary);
  transition: width 0.25s;
  &.menu-open {
    display: block;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  position: relative;
  background: var(--theme-color-primary);
  flex-direction: column;
  width: 44px;
  transition: width 0.25s;
  padding-top: 7px;
  &.menu-open {
    width: 300px;
  }
`;

const NavInnerRoot = styled.nav`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
  padding: 0 10px;
`;

const Toggle = styled.div`
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  cursor: pointer;
  .menu-large & {
    background: #5d6fba;
    transform-style: preserve-3d;
    transition: transform 0.25;
    &:hover {
      background: #4a5dac;
    }
  }
`;

const MenuAction = styled.div`
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  cursor: pointer;
`;

const MenuActionLabel = styled.div`
  color: #ccc;
`;
